import { useState } from "react";
import { usd, fmtDate } from "@/lib/api";
import { useFetch, PageHeader, Panel, StatusBadge, Table, Empty, Loading, FilterTabs } from "@/components/common";
import { ReviewDialog, ActionBtns } from "@/components/admin";
import { waitInfo } from "@/pages/admin/Overview";

const TABS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]];

export function AdminDeposits() {
  const [status, setStatus] = useState("pending");
  const { data, loading, reload } = useFetch(`/admin/deposits${status ? `?status=${status}` : ""}`);
  const [action, setAction] = useState(null);
  const open = (d, kind) => setAction({
    kind, label: "deposit", url: `/admin/deposits/${d.id}/${kind}`,
    summary: <>User <b className="num">{d.user_code}</b> · {usd(d.amount)} via {d.method_label} · Txn <span className="num">{d.txn_id}</span></>,
  });
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin" title="Deposit verification" subtitle="Match the unique amount and transaction ID with your records before approving. Target: verify within 4 hours (never auto-approved)."
        actions={<FilterTabs value={status} onChange={setStatus} options={TABS} testid="deposit-status-filter" />} />
      <Panel>
        {loading ? <Loading /> : (
          <Table head={["Submitted", "Waiting", "User ID", "Tier", "Unique amount", "Method", "Transaction ID", "Status", ""]} testid="admin-deposits-table">
            {!data?.length && <Empty cols={9} text="No deposits" />}
            {data?.map((d) => (
              <tr key={d.id} data-testid={`admin-deposit-row-${d.id}`}>
                <td className="nx-td text-xs">{fmtDate(d.submitted_at || d.created_at)}</td>
                <td className="nx-td">{d.status === "pending" && d.submitted_at ? (() => { const w = waitInfo(d.submitted_at); return <span className={`num rounded-full px-2 py-0.5 text-xs ${w.overdue ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}`} data-testid={`deposit-wait-${d.id}`}>{w.overdue ? "Overdue " : ""}{w.label}</span>; })() : <span className="text-xs text-slate-600">—</span>}</td>
                <td className="nx-td num text-[#10B981]">{d.user_code}</td>
                <td className="nx-td num">{usd(d.tier)}</td>
                <td className="nx-td num text-white font-semibold">{usd(d.amount)}</td>
                <td className="nx-td">{d.method_label}</td>
                <td className="nx-td num text-xs" data-testid={`admin-deposit-txn-${d.id}`}>{d.txn_id || "—"}</td>
                <td className="nx-td"><StatusBadge status={d.status} />{d.admin_note && <p className="mt-1 text-[11px] text-slate-500">{d.admin_note}</p>}</td>
                <td className="nx-td">{d.status === "pending" && <ActionBtns id={d.id} onApprove={() => open(d, "approve")} onReject={() => open(d, "reject")} />}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      <ReviewDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); reload(); }} />
    </div>
  );
}

export function AdminWithdrawals() {
  const [status, setStatus] = useState("pending");
  const { data, loading, reload } = useFetch(`/admin/withdrawals${status ? `?status=${status}` : ""}`);
  const [action, setAction] = useState(null);
  const open = (w, kind) => setAction({
    kind, label: "withdrawal", payout: true, url: `/admin/withdrawals/${w.id}/${kind}`,
    summary: <>User <b className="num">{w.user_code}</b> · Pay {usd(w.net_amount)} via {w.method_label} to <span className="num break-all">{w.destination}</span>{kind === "reject" && " · amount will be refunded"}</>,
  });
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Admin" title="Withdrawal requests" subtitle="Pay out manually, then approve with the payout reference. Rejecting refunds the user."
        actions={<FilterTabs value={status} onChange={setStatus} options={TABS} testid="withdrawal-status-filter" />} />
      <Panel>
        {loading ? <Loading /> : (
          <Table head={["Requested", "User ID", "Amount", "Fee", "Net payout", "Method", "Destination", "Status", ""]} testid="admin-withdrawals-table">
            {!data?.length && <Empty cols={9} text="No withdrawals" />}
            {data?.map((w) => (
              <tr key={w.id} data-testid={`admin-withdrawal-row-${w.id}`}>
                <td className="nx-td text-xs">{fmtDate(w.created_at)}</td>
                <td className="nx-td num text-[#10B981]">{w.user_code}</td>
                <td className="nx-td num text-white">{usd(w.amount)}</td>
                <td className="nx-td num">{usd(w.fee)}</td>
                <td className="nx-td num font-semibold text-white">{usd(w.net_amount)}</td>
                <td className="nx-td">{w.method_label}</td>
                <td className="nx-td max-w-[220px] truncate text-xs" title={w.destination}>{w.destination}</td>
                <td className="nx-td"><StatusBadge status={w.status} />{w.payout_ref && <p className="num mt-1 text-[11px] text-slate-500">{w.payout_ref}</p>}</td>
                <td className="nx-td">{w.status === "pending" && <ActionBtns id={w.id} onApprove={() => open(w, "approve")} onReject={() => open(w, "reject")} />}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      <ReviewDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); reload(); }} />
    </div>
  );
}
